import { FlexBox } from '@fable/components'
import { css, cx } from '@fable/theme'
import { useStatsTheme } from 'app/webview/pages/stats_profile/theme/ctx'
import { StreakMapCellType } from '../types'
import { useChartData } from '../hooks/useChartData'
import { useStats } from 'app/webview/pages/stats_profile/lib/ctx/stats'

const getCalendarDays = (year: number) => {
  return Array.from({ length: 12 }, (_, month) => {
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    return {
      month,
      days: Array.from({ length: daysInMonth }, (_, day) => day + 1),
      firstColumnCutoff: month === 1 ? 14 : 15,
    }
  })
}

export const StreakMapChart = () => {
  const { data } = useChartData()
  const year = useStats((s) => s.year)
  const theme = useStatsTheme((s) => s.theme)
  const calendarDays = getCalendarDays(year)

  const getCellColor = (type: StreakMapCellType) => {
    switch (type) {
      case 'finished':
        return theme.colors.charts.streakMap.finished
      case 'best-streak':
        return theme.colors.charts.streakMap.bestStreak
      case 'days-read':
        return theme.colors.charts.streakMap.daysRead
      default:
        return theme.colors.charts.streakMap.default
    }
  }

  const eventLookup = data.reduce((acc, event) => {
    acc[event.day] = event.type
    return acc
  }, {} as Record<string, string>)

  return (
    <FlexBox
      className={css`
        gap: 3.5px;
      `}
    >
      {calendarDays.map(({ month, days, firstColumnCutoff }) => {
        const firstColumnDays = days.slice(0, firstColumnCutoff)
        const secondColumnDays = days.slice(firstColumnCutoff)

        return (
          <FlexBox
            key={month}
            className={css`
              flex: 1;
              gap: 3.5px;
              display: flex;
              flex-wrap: wrap;
              align-items: flex-end;
            `}
          >
            {/* First Column (First 15 Days) */}
            <FlexBox
              className={cx(
                'anim__streak-map-cell',
                css`
                  flex: 1;
                  display: flex;
                  flex-direction: column-reverse;
                  gap: 3.5px;
                  align-items: center;
                  opacity: 0;
                  will-change: opacity;
                `
              )}
            >
              {firstColumnDays.map((day) => {
                const dateKey = `${year}-${String(month + 1).padStart(
                  2,
                  '0'
                )}-${String(day).padStart(2, '0')}`
                const eventType = eventLookup[dateKey] as StreakMapCellType

                return (
                  <span
                    key={day}
                    className={cx(
                      css`
                        display: block;
                        width: 100%;
                        aspect-ratio: 1 / 1;
                        border-radius: 2px;
                        background-color: ${getCellColor(eventType)};
                      `
                    )}
                  />
                )
              })}
            </FlexBox>

            {/* Second Column (Remaining Days) */}
            <FlexBox
              className={cx(
                'anim__streak-map-cell',
                css`
                  flex: 1;
                  display: flex;
                  flex-direction: column-reverse;
                  gap: 3.5px;
                  align-items: center;
                  opacity: 0;
                  will-change: opacity;
                `
              )}
            >
              {secondColumnDays.map((day) => {
                const dateKey = `${year}-${String(month + 1).padStart(
                  2,
                  '0'
                )}-${String(day).padStart(2, '0')}`
                const eventType = eventLookup[dateKey] as StreakMapCellType

                return (
                  <span
                    key={day}
                    className={cx(
                      css`
                        display: block;
                        width: 100%;
                        aspect-ratio: 1 / 1;
                        border-radius: 2px;
                        background-color: ${getCellColor(eventType)};
                      `
                    )}
                  />
                )
              })}
            </FlexBox>
          </FlexBox>
        )
      })}
    </FlexBox>
  )
}
